<template>
    <div :class="$style.tilesWrapper">
        <div :class="$style.tilesButton">
            <button
                data-interface-target="open-navigation-menu"
                :class="$style.icon_wrapper"
                @click="toggleMore"
            >
                <Icon
                    name="plus"
                    :class="[$style.icon, showMore && $style.icon_rotate]"
                />
            </button>
        </div>

        <div :class="$style.tilesRow">
            <BaseLink
                v-for="item in visibleItems"
                :key="item.id"
                :to="item.to"
                :externalTo="item.externalTo"
                :target="item.target"
                :class="[$style.tile, $style[item.id]]"
            >
                <Typography
                    type="caption_all-caps"
                    semibold
                >
                    {{ item.title }}
                </Typography>
            </BaseLink>
            <button
                v-if="hiddenItems.length && !showMore"
                :class="[$style.tile, $style.moreBtn]"
                @click="toggleMore"
            >
                <Typography
                    type="caption_all-caps"
                    semibold
                >
                    {{ $tr('tiles.more') }}
                </Typography>
            </button>
            <button
                v-else-if="hiddenItems.length && showMore"
                :class="[$style.tile, $style.moreBtn]"
            >
                <Typography
                    type="caption_all-caps"
                    semibold
                >
                    {{ firstHiddenItems.title }}
                </Typography>
            </button>
        </div>
        <div
            v-if="showMore"
            ref="wrapperRef"
            :class="$style.popup"
        >
            <div :class="[$style.tiles_popup, $style.long]">
                <BaseLink
                    v-for="item in restHiddenItems"
                    :key="item.id"
                    :to="item.to"
                    :externalTo="item.externalTo"
                    :target="item.target"
                    :class="[$style.tile, $style.popupTile, $style[item.id]]"
                >
                    <Typography
                        type="caption_all-caps"
                        semibold
                    >
                        {{ item.title }}
                    </Typography>
                </BaseLink>
            </div>

            <div :class="[$style.tiles_popup, $style.short]">
                <BaseLink
                    v-for="(item, index) in allItems"
                    :key="item.id"
                    :to="item.to"
                    :externalTo="item.externalTo"
                    :target="item.target"
                    :class="[
                        $style.tile,
                        $style.popupTile,
                        $style[item.id],
                        animatedIndex >= index && $style.tile_appearing,
                        animatedIndex < index && $style.tile_hide,
                    ]"
                >
                    <Typography
                        type="header"
                        size="xs"
                        semibold
                    >
                        {{ item.title }}
                    </Typography>
                </BaseLink>
            </div>
            <button
                v-if="hiddenItems.length"
                :class="[$style.tile, $style.closeBtn, $style.long]"
                @click="toggleMore"
            >
                <Typography
                    type="caption_all-caps"
                    accent
                >
                    {{ $tr('tiles.close') }}
                </Typography>
            </button>
        </div>
    </div>
</template>

<script setup lang="ts">
import { TransitionPresets, useTransition } from '@vueuse/core'

import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'

import BaseLink from '@/components_new/BaseLink.vue'
import Icon from '@/components_new/Icon/Icon.vue'
import Typography from '@/components_new/Typography.vue'
import { useTr } from '@/composables/use-tr'
import { ROUTE_NAMES } from '@/router/constants'
import { DEVELOPERS_LANDPAGE_PATH, NavLink, PARTNERS_LANDPAGE_PATH } from '@/router/navigation-constants'

const tr = useTr()

const allItems = computed((): (NavLink & { id: string })[] => [
    {
        title: tr('tiles.add_games'),
        id: 'developers',
        externalTo: DEVELOPERS_LANDPAGE_PATH,
        target: '_blank',
    },
    {
        title: tr('tiles.publishers'),
        id: 'publishers',
        externalTo: PARTNERS_LANDPAGE_PATH,
        target: '_blank',
    },
    {
        title: tr('tiles.contacts'),
        id: 'contact_us',
        to: { name: ROUTE_NAMES.CONTACTS },
    },
    {
        title: tr('tiles.legal'),
        id: 'legal',
        to: { name: ROUTE_NAMES.TERMS_OF_USE },
    },
])

const wrapperRef = ref<HTMLElement | null>(null)
const buttonRef = ref<HTMLElement | null>(null)
const visibleCount = computed(() => (allItems.value.length > 4 ? 3 : allItems.value.length))
const showMore = ref(false)

const visibleItems = computed(() => allItems.value.slice(0, visibleCount.value))
const hiddenItems = computed(() => allItems.value.slice(visibleCount.value))
const firstHiddenItems = computed(() => hiddenItems.value[0])
const restHiddenItems = computed(() => hiddenItems.value.slice(1))

const transitionIndex = ref(0)

const animatedIndex = useTransition(transitionIndex, {
    duration: 100,
    transition: TransitionPresets.easeOutExpo,
})

function toggleShow(forceHide?: boolean) {
    if (forceHide === false) {
        showMore.value = false
        return
    }
    showMore.value = !showMore.value
}

function toggleMore(e: MouseEvent) {
    buttonRef.value = e.currentTarget as HTMLElement
    toggleShow()
}

function onClickOutside(event: MouseEvent) {
    if (!showMore.value) {
        return
    }
    const target = event.target as Node

    const clickedOutsidePopup = !wrapperRef.value?.contains(target)
    const clickedOutsideButton = !buttonRef.value?.contains(target)

    if (clickedOutsidePopup && clickedOutsideButton) {
        toggleShow(false)
    }
}

onMounted(() => {
    document.addEventListener('click', onClickOutside)
})

onBeforeUnmount(() => {
    document.removeEventListener('click', onClickOutside)
})

watch(showMore, (visible) => {
    if (visible) {
        transitionIndex.value = allItems.value.length - 1
    } else {
        transitionIndex.value = 0
    }
})
</script>

<style module>
.tilesWrapper {
    position: relative;
    --tiles-gap: 2px;
}
.tilesButton {
    display: block;
}
.tilesRow {
    display: none;
}
.icon {
    color: black;
    transition: all 0.13s ease-in-out;
}
.icon_rotate {
    transform: rotate(-45deg);
}
.icon_wrapper:has(.icon_rotate) {
    background: conic-gradient(
        from 270deg at 50% 50%,
        #ff9c9c 0deg,
        #b099d0 113.39999914169312deg,
        #9ce52e 233.62456798553467deg,
        #ffc850 360deg
    );
}
.icon_wrapper {
    cursor: pointer;
    border-radius: 50%;
    padding: 14px;
    background: conic-gradient(
        from 90deg at 50% 50%,
        #ffc850 0deg,
        #9ce52e 126.37deg,
        #b099d0 246.6deg,
        #ff9c9c 360deg
    );
}
.tile {
    padding: 0 8px;
    border: none;
    border-radius: 8px;
    text-transform: uppercase;
    height: 25px;
    cursor: pointer;
    display: flex;
    align-items: center;
    white-space: nowrap;
    text-align: center;
    color: black;
    justify-content: flex-end;
}
.tile:hover,
.tile:focus {
    border-radius: 12px;
}
.popup {
    position: absolute;
    right: 0;
    padding: 8px 0;
    z-index: 1;
    display: flex;
    justify-content: flex-end;
    flex-direction: column;
    align-items: flex-end;
    gap: var(--tiles-gap);
}
.moreBtn {
    background: #e6d4ff;
}
.developers {
    background: #9ce52e;
}
.publishers {
    background: var(--purple-600);
}
.contact_us {
    background: #ffc3c3;
}
.legal {
    background: #c3bce0;
}
.tiles_popup {
    justify-content: flex-end;
    flex-direction: column;
    align-items: flex-end;
    gap: var(--tiles-gap);
}
.tiles_popup.short {
    display: flex;
    gap: 8px;
}
.tiles_popup.long {
    display: none;
}
.closeBtn {
    background: black;
    color: white;
    display: none;
}

.tiles_popup.short .tile {
    padding: 10px 16px;
    border-radius: 1000px;
    transition:
        transform 0.13s,
        opacity 0.13s;
}

.tiles_popup.short .tile_appearing {
    opacity: 1;
    transform: translateY(0);
}
.tiles_popup.short .tile_hide {
    opacity: 0;
    transform: translateY(-8px);
}

.tiles_popup.short .tile:hover,
.tiles_popup.short .tile:focus {
    padding: 10px 16px;
    border-radius: 1000px;
    transform: scale(1.1);
}

.tiles_popup.short .tile:has(~ .tile:hover),
.tiles_popup.short .tile:has(~ .tile:has(:focus)) {
    transform: translateY(calc(var(--tiles-gap) * -1));
}

.tiles_popup.short .tile:hover ~ .tile,
.tiles_popup.short .tile:has(:focus) ~ .tile {
    transform: translateY(var(--tiles-gap));
}

@media (--desktop-medium-plus) {
    .tilesWrapper {
        max-width: 200px;
    }
    .popup {
        padding: var(--tiles-gap) 0;
    }
    .tilesRow {
        display: flex;
        flex-wrap: wrap;
        justify-content: flex-end;
        gap: var(--tiles-gap);
    }

    .tiles_popup.short {
        display: none;
    }

    .tiles_popup.long {
        display: flex;
        flex-direction: row;
    }

    .tilesButton {
        display: none;
    }

    .closeBtn {
        display: block;
    }
}
</style>
